import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Train, Search, MapPin, Loader2, Utensils } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function FoodOnTrain() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [train, setTrain] = useState(null);
  const [busy, setBusy] = useState(false);
  const [station, setStation] = useState(null);
  const [rests, setRests] = useState([]);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter your PNR or train number");
    setBusy(true); setTrain(null); setStation(null); setRests([]);
    try {
      const body = /^\d{5}$/.test(q.trim()) ? { train_no: q.trim() } : { pnr: q.trim() };
      const { data } = await api.post("/train/search", body);
      setTrain(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const pickStation = async (s) => {
    setStation(s);
    try { const { data } = await api.get(`/train/station/${s.code}/restaurants`); setRests(data); } catch { setRests([]); }
  };

  const order = (r) => {
    localStorage.setItem("bitego_train_ctx", JSON.stringify({ train: train.name, pnr: train.pnr, coach: train.coach, seat: train.seat, station: station.name, eta: station.eta }));
    nav(`/restaurant/${r.id}`);
  };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-10" data-testid="food-on-train">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg flex items-center gap-2"><Train size={20} className="text-coral" /> Food on Train</h1>
      </div>
      <div className="px-4 py-4">
        <div className="bg-white rounded-2xl border border-border p-4">
          <p className="text-sm text-ink-600 mb-2">Enter your <b>PNR</b> (10-digit) or <b>train number</b> to get hot meals delivered to your seat.</p>
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="PNR or train no. (try 12723)" className="flex-1 bg-surface rounded-xl px-3.5 py-3 text-sm outline-none focus:ring-2 ring-coral" data-testid="train-search-input" />
            <button onClick={search} disabled={busy} className="bg-coral text-white font-bold px-4 rounded-xl flex items-center gap-1" data-testid="train-search-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}</button>
          </div>
        </div>

        {train && (
          <div className="bg-white rounded-2xl border border-border p-4 mt-3" data-testid="train-result">
            <div className="font-display font-bold text-ink">{train.name} · {train.train_no}</div>
            <div className="text-xs text-ink-600">{train.from} → {train.to}{train.coach ? ` · Coach ${train.coach}, Seat ${train.seat}` : ""}</div>
            <div className="mt-3 text-xs font-bold text-ink-600 uppercase">Upcoming stations</div>
            <div className="mt-2 space-y-2">
              {train.stations.map((s) => (
                <button key={s.code} onClick={() => pickStation(s)} className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 border text-left ${station?.code === s.code ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`train-station-${s.code}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold"><MapPin size={14} className="text-coral" /> {s.name}</span>
                  <span className="text-xs text-ink-600">ETA {s.eta}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {station && (
          <div className="mt-3">
            <div className="text-sm font-bold text-ink mb-2 flex items-center gap-2"><Utensils size={16} className="text-coral" /> Restaurants delivering at {station.name}</div>
            <div className="space-y-2">
              {rests.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-border p-3 flex items-center gap-3" data-testid={`train-rest-${r.id}`}>
                  <img src={r.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0"><div className="font-bold text-sm text-ink truncate">{r.name}</div><div className="text-xs text-ink-600 truncate">{r.cuisine}</div></div>
                  <button onClick={() => order(r)} className="bg-coral text-white font-bold text-sm px-3 py-2 rounded-xl" data-testid={`train-order-${r.id}`}>Order</button>
                </div>
              ))}
              {!rests.length && <div className="text-sm text-ink-600 text-center py-4">No restaurants at this station yet.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
